/**
 * Hard reload — drop every client-side cache and fetch the app fresh.
 *
 * A plain `location.reload()` is not enough on the tablets: a stale service
 * worker or Cache Storage entry can hand back the OLD bundle, and a proxy/CDN
 * copy of index.html can do the same. So: clear Cache Storage, unregister any
 * service worker, then navigate with a cache-busting query so the document
 * request cannot be served from a stale copy.
 *
 * Used by BOTH remote refresh (the portal's "refresh resident tablets" command)
 * and the manual retry button on the admin-setup screen — an installer standing
 * at the tablet and an operator in the portal should get identical behaviour.
 */
export async function hardReload(path?: string): Promise<void> {
  try {
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* Cache API unavailable (older Android WebView) — nothing to clear. */
  }

  try {
    // Undefined in an Android WebView; optional-chained rather than assumed.
    if (navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* SW API unavailable — nothing to unregister. */
  }

  try {
    const url = new URL(window.location.href);
    if (path) {
      url.pathname = path;
      url.search = '';
      url.hash = '';
    }
    url.searchParams.set('_v', Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}
